import { useRef } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';

import { useMagicIslandController } from './MagicIslandContext';
import { PaginationIslandContent } from './modes/PaginationIslandContent';
import { PillIslandContent } from './modes/PillIslandContent';
import type { IslandMode } from './types';

/**
 * The surface a mode renders on. Link and CTA share the dark-pill surface so
 * swapping between them (and between two links) keeps the same React element —
 * which is what lets the pill morph its width instead of remounting. Custom
 * modes may opt into the same surface (`surface: 'pill'`) and tag their glass
 * with ISLAND_GLASS_LAYOUT_ID so the pill morphs across the swap too (bulk
 * enquiry does this). Pagination is its own surface, so switching to/from it
 * pops out then in.
 */
const surfaceKey = (mode: IslandMode): string => {
    if (mode.kind === 'pagination') return 'pagination';
    if (mode.kind === 'custom') return mode.surface ?? 'custom';
    return 'pill';
};

/**
 * The Magic Island shell — a fixed, bottom-centre wrapper that renders whatever
 * `activeMode` the controller picks (Apple AirPods-Max style). Transparent and
 * `pointer-events-none`; each surface brings its own frosted glass.
 *
 * Animation (matching Apple):
 * - Entry pops up from below and **overshoots** a few px past the target, then
 *   bounds back — an underdamped spring.
 * - Hiding fades the whole shell back down.
 * - The shell is driven by show/hide on a single, always-mounted wrapper rather
 *   than by mounting/unmounting through AnimatePresence: on a fast scroll the
 *   active mode can flip pill → hidden → pill faster than an exit completes, and
 *   unmounting on `hidden` would strand an exiting pill (same key) that never
 *   finished, leaving a ghost stuck on screen. Keeping it mounted and animating
 *   opacity/offset can't strand anything. `shownRef` holds the last visible mode
 *   so the surface fades out in place instead of vanishing instantly.
 * - AnimatePresence still sequences the rarer surface swap (pagination ↔ pill),
 *   where the two glass surfaces genuinely differ and must exit → enter.
 * - `prefers-reduced-motion` collapses every transition to instant.
 */
export const MagicIsland = () => {
    const controller = useMagicIslandController();
    const reduce = useReducedMotion() ?? false;
    const mode: IslandMode = controller?.activeMode ?? { kind: 'hidden' };
    const isHidden = mode.kind === 'hidden';

    // Remember the last visible mode so the shell can fade it out in place when
    // the controller hides it, instead of unmounting (see the note above).
    const shownRef = useRef<IslandMode>({ kind: 'hidden' });
    if (!isHidden) shownRef.current = mode;
    const shown = shownRef.current;

    // Underdamped → overshoots above the target, then settles (the Apple bounce).
    const popSpring = reduce
        ? { duration: 0 }
        : ({ type: 'spring', stiffness: 520, damping: 21, mass: 1 } as const);
    const exitTween = reduce
        ? { duration: 0 }
        : ({ duration: 0.18, ease: [0.4, 0, 1, 1] } as const);

    const renderSurface = (current: IslandMode) => {
        switch (current.kind) {
            case 'pagination':
                return <PaginationIslandContent mode={current} />;
            case 'link':
            case 'cta':
            case 'purchase':
                return (
                    <PillIslandContent
                        mode={current}
                        reduce={reduce}
                        transition={popSpring}
                    />
                );
            case 'custom':
                return current.content;
            case 'hidden':
                return null;
        }
    };

    return (
        <div
            // Floating overlay content still needs a landmark so assistive
            // tech can reach it from the landmark list (axe `region`) — but a
            // hidden island only fades out in place, so it must drop out of
            // the landmark list and the focus order too.
            role={isHidden ? undefined : 'complementary'}
            aria-label={isHidden ? undefined : 'Quick actions'}
            inert={isHidden}
            className="pointer-events-none fixed inset-x-0 bottom-8 z-fixed flex justify-center max-md:bottom-24"
        >
            <motion.div
                initial={false}
                animate={
                    isHidden
                        ? { opacity: 0, y: 20, scale: 0.94 }
                        : { opacity: 1, y: 0, scale: 1 }
                }
                transition={isHidden ? exitTween : popSpring}
                className="pointer-events-none"
            >
                <AnimatePresence mode="wait" initial={false}>
                    <motion.div
                        key={surfaceKey(shown)}
                        initial={
                            reduce ? false : { opacity: 0, y: 28, scale: 0.9 }
                        }
                        animate={{
                            opacity: 1,
                            y: 0,
                            scale: 1,
                            transition: popSpring,
                        }}
                        exit={
                            reduce
                                ? { opacity: 0 }
                                : {
                                      opacity: 0,
                                      y: 20,
                                      scale: 0.94,
                                      transition: exitTween,
                                  }
                        }
                        className="pointer-events-none"
                    >
                        {renderSurface(shown)}
                    </motion.div>
                </AnimatePresence>
            </motion.div>
        </div>
    );
};
